import { FpAxios } from '@/services/FpAxios';
import { AxiosInstance } from 'axios';
import { LandOfWork, LandOfWorkDto } from '@/models/LandOfWork';
import { Address } from '@/modules/multiForm/shared/MultiformModels';
import { IApi } from '@/models/interfaces/IApi';
import { HoursOfWorkListDto, HoursWithUsersDto } from '@/models/HoursOfWork';
import { UserListElement } from '@/models/ppl/UserListElement';
import { ProductType } from '@/commons/enums/ProductType';
import { Place } from '@/models/PlaceDto';
import processProxy from '@/env/processProxy';


interface GetLandsOfWorkRequest {
  lands: LandOfWorkDto[]
  userId: number
}

export class HoursOfWorkParams {
  duration?: number;
  from?: Date;
  userId?: number;
  districtId?: number;
  landId?: number;
  productType?: ProductType[] = [];
  language?: ProductType[] = [];
  numberOfDays?: number;
  users?: number[];
  widgetOwnerId?: Nullable<number>;
  bookTimeBefore?: boolean;
  extendBusinessHours?: boolean;
  excludeIds?: number[]; // idki spotkań które mają być pominięte -  wyświetlać się jako wolne
};
// Interface for user logs response based on the API documentation
export interface UserLogEntry {
  log: string;
  logType: string;
  createdAt: string;
  ip: string;
  userAgent: string;
}
export interface UserLanguage {
  value: string;
  label: string;
}

export interface UserGender {
  value: string;
  label: string;
}

export interface UserZitadel {
  id: string;
  username: string;
  isActive: boolean;
  isLocked: boolean;
  email: string;
  phone: string | null;
  givenName: string;
  familyName: string;
  preferredLanguage: UserLanguage;
  gender: UserGender;
}

export interface PasswordlessMethod {
  id: string;
  name: string;
}

export interface U2fMethod {
  id: string;
  name: string;
}

export enum MfaType {
  U2F = 'U2f',
  PASSWORDLESS = 'passwordless',
  OTP_SMS = 'OtpSms',
  TOTP = 'Totp',
  PASSWORD = 'Password',
}
//TODO: typowanie
class UserManagementApi implements IApi {
  public readonly baseUrl: string = 'users';
  public readonly fpAxios: AxiosInstance = FpAxios.create();
  private readonly userManagementAxios = FpAxios.create({ baseURL: processProxy.VUE_APP_BASE_USER_MANAGEMENT_API_URL })

  async saveOfficeAddress(userId: number, address: Address): Promise<void> {
    await this.fpAxios.put<Address>(`${this.baseUrl}/${userId}/office/address`, address);
  }

  async getOfficeAddress(userId: number, isOnlyOfficeAddress: boolean = false): Promise<Address | null> {
    const response = await this.fpAxios.get<Address>(`${this.baseUrl}/${userId}/office/address?isOnlyOfficeAddress=${isOnlyOfficeAddress}`);
    return response.data;
  }

  async getOfficeAddressFromAddressId(addressId: number): Promise<Address | null> {
    const response = await this.fpAxios.get<Address>(`${processProxy.VUE_APP_BASE_URL}address/${addressId}`);
    return response.data;
  }

  async getLandsOfWork(userId: number): Promise<LandOfWork[]> {
    try {
      const response = await this.fpAxios.get<GetLandsOfWorkRequest[]>(`${this.baseUrl}/location/range`, {
        params: {
          user_id: userId,
        },
      });
      const find = response.data.find(x => x.userId === userId);
      return find ? find.lands.map(dto => new LandOfWork(dto)) : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async getHoursOfWorkByLocalization(config: HoursOfWorkParams): Promise<HoursWithUsersDto> {
    const response = await this.fpAxios.post<HoursWithUsersDto>('activities/availability', {
      useDefaultBusinessHours: true,
      ...config,
    });
    return {
      branches: response.data.branches,
      places: response.data.places.map(place => new Place(place)),
      users: response.data.users
        .map(x => new UserListElement(x)),
      days: response.data.days,
    };
  }

  async getHoursOfWork(userId: number, config?: HoursOfWorkParams): Promise<HoursOfWorkListDto[]> {
    try {
      const response = await this.fpAxios.post<HoursOfWorkListDto[]>('activities/availability', {
        userId,
        useDefaultBusinessHours: true,
        from: config?.from?.toISOString(),
        numberOfDays: config?.numberOfDays,
        duration: config?.duration,
        bookTimeBefore: config?.bookTimeBefore,
        extendBusinessHours: config?.extendBusinessHours,
        widgetOwnerId: config?.widgetOwnerId || null,
        excludeIds: config?.excludeIds || undefined,
      });
      return response.data;
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async getUserZitadelActivityLogs(userId: string): Promise<{ data: UserLogEntry[] }> {
    try {
      const response = await this.userManagementAxios.get(`users/${userId}/logs`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user logs:', error);
      return { data: [], };
    }
  }

  async getUserZitadel(userId: string): Promise<Nullable<UserZitadel>> {
    try {
      const response = await this.userManagementAxios.get<UserZitadel>(`users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching zitadel user:', error);
      return null;
    }
  }

  async unlockUserZitadel(userId: string): Promise<{ locked: boolean } | null> {
    try {
      const response = await this.userManagementAxios.put(`users/${userId}/locked`, {
        locked: false,
      });
      return response.data;
    } catch (error) {
      console.error('Error unlocking user:', error);
      return null;
    }
  }//TODO: OGARNAC PLIK I TYPY

  async getMyMfa(): Promise<any> {
    try {
      const response = await this.userManagementAxios.get('users/my/mfa');
      return response.data;
    } catch (error) {
      console.error('Error fetching zitadel user:', error);
      return null;
    }
  }

  async getUserMfa(userId: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.get(`users/${userId}/mfa`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user mfa:', error);
      return null;
    }
  }

  async createMyUserMfaOtpSms(): Promise<any> {
    try {
      const response = await this.userManagementAxios.post('users/my/mfa/otp_sms');
      return response.data;
    } catch (error) {
      console.error('Error fetching zitadel user:', error);
      return null;
    }
  }

  async removeMyUserMfaOtpSms(): Promise<any> {
    try {
      const response = await this.userManagementAxios.delete('users/my/mfa/otp_sms');
      return response.data;
    } catch (error) {
      console.error('Error removing zitadel user:', error);
      return null;
    }
  }

  async createMyUserMfaU2F(): Promise<any> {
    try {
      const response = await this.userManagementAxios.post('users/my/mfa/u2f', {
        domain: processProxy.VUE_APP_ZITADEL_PASSKEY_RP_ID || 'lendi.pl',
      });
      return response.data;
    } catch (error) {
      console.error('Error creating zitadel user:', error);
      return null;
    }
  }

  async verifyMyUserMfaU2F(u2fId: string, publicKeyCredential: PublicKeyCredential, name: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.post(`users/my/mfa/u2f/${u2fId}/verify`,
        {
          publicKeyCredential,
          name,
        },
      );
      // 204/empty JSON can yield null/undefined data; callers must not treat that as failure.
      return response.data ?? true;
    } catch (error) {
      console.error('Error verifying zitadel user:', error);
      return null;
    }
  }

  async removeMyUserMfaU2F(u2fId: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.delete(`users/my/mfa/u2f/${u2fId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing zitadel user:', error);
      return null;
    }
  }

  async createMyUserMfaTotp(): Promise<{
    uri: string;
    secret: string;
  }> {
    try {
      const response = await this.userManagementAxios.post('users/my/mfa/totp');
      return response.data;
    } catch (error) {
      console.error('Error creating TOTP for user:', error);
      return {
        uri: '',
        secret: '',
      };
    }
  }

  async verifyMyUserMfaTotp(code: string): Promise<any> {
    const response = await this.userManagementAxios.post('users/my/mfa/totp/verify', {
      code: code,
    });
    return response.data;
  }

  async removeMyUserMfaTotp(): Promise<any> {
    try {
      const response = await this.userManagementAxios.delete('users/my/mfa/totp');
      return response.data;
    } catch (error) {
      console.error('Error removing TOTP for user:', error);
      return null;
    }
  }

  async removeUserMfaTotp(userId: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.delete(`users/${userId}/mfa/totp`);
      return response.data;
    } catch (error) {
      console.error('Error removing TOTP for user:', error);
      return null;
    }
  }

  async removeUserMfaOtpSms(userId: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.delete(`users/${userId}/mfa/otp_sms`);
      return response.data;
    } catch (error) {
      console.error('Error removing OTP SMS for user:', error);
      return null;
    }
  }

  async removeUserMfaU2F(userId: string, u2fId: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.delete(`users/${userId}/mfa/u2f/${u2fId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing U2F for user:', error);
      return null;
    }
  }

  // Passwordless Passkeys API methods
  async getMyUserPasswordlessPasskeys(): Promise<any> {
    try {
      const response = await this.userManagementAxios.get('users/my/passwordless/passkeys');
      return response.data;
    } catch (error) {
      console.error('Error fetching passwordless passkeys:', error);
      return null;
    }
  }

  async createMyUserPasswordlessPasskey(domain: string = processProxy.VUE_APP_ZITADEL_PASSKEY_RP_ID || 'lendi.pl', authenticatorType: string = 'Platform'): Promise<any> {
    try {
      const response = await this.userManagementAxios.post('users/my/passwordless/passkeys', {
        domain,
        authenticatorType,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating passwordless passkey:', error);
      return null;
    }
  }

  async verifyMyUserPasswordlessPasskey(passKeyId: string, publicKeyCredential: any, name: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.post(`users/my/passwordless/passkeys/${passKeyId}/verify`, {
        publicKeyCredential,
        name,
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying passwordless passkey:', error);
      return null;
    }
  }

  async deleteMyUserPasswordlessPasskey(passKeyId: string): Promise<any> {
    try {
      const response = await this.userManagementAxios.delete(`users/my/passwordless/passkeys/${passKeyId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting passwordless passkey:', error);
      return null;
    }
  }
}

export default new UserManagementApi();
